/**
 * SAP account group codes (KNA1 field KTOKD, stored in `customers.account_group`).
 *
 * Authored as name -> code so call sites read `ACCOUNT_GROUPS.z001` instead of the opaque `'Z001'`
 * magic string. The code is what SAP sends and what gets persisted; the name is the developer-facing
 * handle and doubles as the i18n key (translate it under the `accountGroup` block), so the UI shows
 * "BAT Sold-to party" and no one ever authors or translates `Z001`.
 *
 * `BAT Employee` is a genuine code value (not a Z-prefixed one), kept verbatim.
 */
export const ACCOUNT_GROUPS = {
  z001: 'Z001',
  z002: 'Z002',
  z003: 'Z003',
  z004: 'Z004',
  z005: 'Z005',
  z006: 'Z006',
  z007: 'Z007',
  batEmployee: 'BAT Employee',
  zint: 'ZINT',
  zlea: 'ZLEA',
  zntc: 'ZNTC',
  zplt: 'ZPLT',
  zscn: 'ZSCN',
} as const

export type AccountGroupName = keyof typeof ACCOUNT_GROUPS
export type AccountGroupCode = (typeof ACCOUNT_GROUPS)[AccountGroupName]

export const ACCOUNT_GROUP_NAMES = Object.keys(ACCOUNT_GROUPS) as AccountGroupName[]
export const ACCOUNT_GROUP_CODES = Object.values(ACCOUNT_GROUPS) as AccountGroupCode[]

export const ACCOUNT_GROUP_NAME_BY_CODE = Object.fromEntries(
  Object.entries(ACCOUNT_GROUPS).map(([name, code]) => [code, name]),
) as Record<AccountGroupCode, AccountGroupName>

export function isAccountGroupCode(value: string | null | undefined): value is AccountGroupCode {
  return value != null && ACCOUNT_GROUP_NAME_BY_CODE[value as AccountGroupCode] !== undefined
}

export function getAccountGroupName(code: string): AccountGroupName | string {
  return ACCOUNT_GROUP_NAME_BY_CODE[code as AccountGroupCode] ?? code
}
